<template>
  <Transition name="slide">
    <div v-if="isOpen" class="pdf-container" :class="{ 'mobile': isMobile }">
      <div class="fixed-header">
        <button @click="close" class="close-pdf mx-2">
          <i class="fas fa-times"></i>
        </button>        
        <div class="pdf-toolbar">
          <button @click="previousPage" :disabled="currentPage === 1">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span>Page {{ currentPage }}</span>
          <button @click="nextPage">
            <i class="fas fa-chevron-right"></i>
          </button>
          <input 
            type="number" 
            v-model.number="currentPage" 
            :min="1" 
            :max="totalPages"
            class="page-input"
          >
        </div>
      </div>
      
      <div class="pdf-wrapper">
        <LoadingSmall v-if="loading" title="Loading PDF..." />
        <VuePdfEmbed
          :source="source"
          :page="currentPage"
          :style="pdfStyle"
          @rendered="onPdfRendered"
          @error="loading = false"
          @pageCount="totalPages = $event"
        />
      </div>
    </div>
  </Transition>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed'
import { useWindowSize } from '@vueuse/core'
import LoadingSmall from '@/components/LoadingSmall.vue'

export default {
  name: 'PdfViewer',
  components: { VuePdfEmbed, LoadingSmall },

  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    source: {
      type: String,
      required: true
    },
    page: {
      type: Number,
      default: 1
    }
  },

  data() {
    return {
      loading: true,
      currentPage: this.page,
      totalPages: 0
    }
  },

  watch: {
    page(newPage) {
      this.currentPage = newPage
    },
    currentPage(newPage) {
      this.$emit('update:page', newPage)
    }
  },

  setup() {
    const { width } = useWindowSize()
    return { width }
  },

  computed: {
    isMobile() {
      return this.width < 768
    },
    pdfStyle() {
      return {
        height: this.isMobile ? 'calc(90vh - 2rem)' : 'calc(100vh - 2rem)',
        width: '100%'
      }
    }
  },

  methods: {
    close() {
      this.$emit('update:isOpen', false)
    },
    nextPage() {
        this.currentPage++
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    onPdfRendered() {
      this.loading = false
    }
  }
}
</script>

<style scoped>
/* Main Container */
.pdf-container {
  position: fixed;
  top: 0;
  right: 0;
  width: 50%;
  height: 100vh;
  background: #ffffff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  padding: 1.5rem;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.dark-mode .pdf-container {
  background: #343541;
  box-shadow: -4px 0 20px rgba(0,0,0,0.18);
}

.pdf-container.mobile {
  width: 100%;
  left: 0;
  padding: 1rem;
}

/* Header & Toolbar */
.fixed-header {
  position: sticky;
  top: 0;
  z-index: 1002;
  background: #ffffff;
  padding: 1rem;
  margin: -1.5rem -1.5rem 0;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.dark-mode .fixed-header {
  background: #444654;
  border-bottom: 1px solid #4B5563;
}

.pdf-toolbar {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.875rem 1.25rem;
  background: #f8fafc;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  flex: 1;
  transition: all 0.3s ease;
}

.dark-mode .pdf-toolbar {
  background: #393950;
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
}

/* Buttons & Controls */
.close-pdf {
  background: #f8fafc;
  border: none;
  cursor: pointer;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #64748b;
}

.dark-mode .close-pdf {
  background: #393950;
  color: #ECECF1;
}

.close-pdf:hover {
  background: #f1f5f9;
  color: #0f172a;
  transform: scale(1.05);
}

.dark-mode .close-pdf:hover {
  background: #444654;
  color: #fff;
}

.pdf-toolbar button {
  background: transparent;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #64748b;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.dark-mode .pdf-toolbar button {
  color: #ECECF1;
}

.pdf-toolbar button:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.dark-mode .pdf-toolbar button:hover:not(:disabled) {
  background: #444654;
  color: #fff;
}

.pdf-toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-input {
  width: 4rem;
  padding: 0.375rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
  font-size: 0.875rem;
  background: #fff;
  color: #2c3e50;
  transition: all 0.2s ease;
}

.dark-mode .page-input {
  background: #232325;
  color: #ECECF1;
  border: 1px solid #4B5563;
}

/* PDF Content */
.pdf-wrapper {
  height: calc(100% - 4rem);
  margin-top: 1rem;
}

/* Animations */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Mobile Specific */
@media (max-width: 768px) {
  .fixed-header {
    padding: 0.75rem;
    margin: -1rem -1rem 0;
    gap: 0.75rem;
  }

  .close-pdf {
    width: 2.25rem;
    height: 2.25rem;
  }

  .pdf-toolbar {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .slide-enter-from,
  .slide-leave-to {
    transform: translateY(100%);
  }
}
</style>


